import { useRef } from 'react';
import { COLOR_PRESETS } from '@/hooks/useStoreSettings';
import {
  LOGO_SHAPES, LOGO_TEXT_STYLES, BRAND_ICONS,
  HERO_IMAGE_PRESETS, BG_PATTERNS, FONT_OPTIONS, RADIUS_OPTIONS,
} from './designConstants';
import { IdentitySettings, DesignSettings, SectionSaveState } from './designTypes';
import { SaveButton, LogoPreview } from './DesignShared';

interface Props {
  identity: IdentitySettings;
  design: DesignSettings;
  updateIdentity: (patch: Partial<IdentitySettings>) => void;
  identitySaveState: SectionSaveState;
  onSaveIdentity: () => void;
  selectedColor: typeof COLOR_PRESETS[0];
  selectedFont: typeof FONT_OPTIONS[0];
  selectedRadius: typeof RADIUS_OPTIONS[0];
}

export default function IdentitySection({
  identity, design, updateIdentity,
  identitySaveState, onSaveIdentity,
  selectedColor, selectedFont, selectedRadius,
}: Props) {
  const logoFileRef = useRef<HTMLInputElement>(null);
  const selectedHeroPreset = HERO_IMAGE_PRESETS.find(h => h.id === identity.heroImageId) ?? HERO_IMAGE_PRESETS[0];

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const url = ev.target?.result as string;
      updateIdentity({ logoImageUrl: url, useLogoImage: true });
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex flex-col gap-5">

      {/* ── Logo Section ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center bg-[#1a1a1a] rounded-lg">
            <i className="ri-store-2-line text-white text-[10px]"></i>
          </div>
          شعار المتجر (Logo)
        </h3>
        <p className="text-xs text-gray-400 mb-4">خصّص شكل ونص شعار متجرك</p>

        {/* Live Logo Preview */}
        <div className="bg-[#0a0a0a] rounded-xl p-5 mb-4 flex items-center justify-center">
          <LogoPreview identity={identity} selectedColor={selectedColor} selectedFont={selectedFont} />
        </div>

        {/* Logo Text */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-gray-500">اسم المتجر (عربي)</label>
            <input
              type="text"
              value={identity.logoText}
              onChange={e => updateIdentity({ logoText: e.target.value })}
              placeholder="إبداع"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-gray-500">النص الثانوي (إنجليزي)</label>
            <input
              type="text"
              value={identity.logoSubtext}
              onChange={e => updateIdentity({ logoSubtext: e.target.value })}
              placeholder="Game Store"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white"
            />
          </div>
        </div>

        {/* Logo Text Style */}
        <div className="mb-4">
          <p className="text-xs font-bold text-gray-500 mb-2">لون نص الشعار</p>
          <div className="grid grid-cols-4 gap-2">
            {LOGO_TEXT_STYLES.map(style => (
              <button
                key={style.id}
                onClick={() => updateIdentity({ logoTextStyle: style.id })}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                  identity.logoTextStyle === style.id
                    ? 'border-[#1a1a1a] bg-white'
                    : 'border-gray-100 bg-white hover:border-gray-300'
                }`}
              >
                <div
                  className="text-sm font-black"
                  style={
                    style.id === 'gradient'
                      ? { background: `linear-gradient(to left, ${selectedColor.primaryLight}, ${selectedColor.primary})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }
                      : style.id === 'white'
                      ? { color: '#6b7280' }
                      : style.id === 'gold'
                      ? { color: '#f59e0b' }
                      : { color: '#1a1a1a' }
                  }
                >
                  أ
                </div>
                <span className="text-[10px] text-gray-500 font-medium">{style.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Logo Icon */}
        <div className="mb-4">
          <p className="text-xs font-bold text-gray-500 mb-2">أيقونة الشعار</p>
          <div className="grid grid-cols-6 gap-2">
            {BRAND_ICONS.map(icon => (
              <button
                key={icon.id}
                onClick={() => updateIdentity({ logoIconId: icon.id, useLogoImage: false })}
                title={icon.label}
                className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border-2 transition-all cursor-pointer ${
                  identity.logoIconId === icon.id && !identity.useLogoImage
                    ? 'border-[#1a1a1a] bg-white'
                    : 'border-gray-100 bg-white hover:border-gray-300'
                }`}
              >
                <div
                  className="w-7 h-7 flex items-center justify-center rounded-lg"
                  style={{ background: `linear-gradient(135deg, ${selectedColor.primaryLight}, ${selectedColor.primary})` }}
                >
                  <i className={`${icon.icon} text-sm`} style={{ color: selectedColor.textOnPrimary }}></i>
                </div>
                <span className="text-[9px] text-gray-400">{icon.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Logo Shape */}
        <div className="mb-4">
          <p className="text-xs font-bold text-gray-500 mb-2">شكل أيقونة الشعار</p>
          <div className="grid grid-cols-4 gap-2">
            {LOGO_SHAPES.map(shape => (
              <button
                key={shape.id}
                onClick={() => updateIdentity({ logoShape: shape.id })}
                className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                  identity.logoShape === shape.id
                    ? 'border-[#1a1a1a] bg-white'
                    : 'border-gray-100 bg-white hover:border-gray-300'
                }`}
              >
                <div
                  className="w-8 h-8"
                  style={{
                    background: `linear-gradient(135deg, ${selectedColor.primaryLight}, ${selectedColor.primary})`,
                    borderRadius: shape.radius,
                  }}
                ></div>
                <span className="text-[10px] text-gray-500 font-medium">{shape.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Upload Logo Image */}
        <div className="mb-2">
          <p className="text-xs font-bold text-gray-500 mb-2">رفع صورة شعار مخصصة</p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => logoFileRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-[#1a1a1a] hover:text-[#1a1a1a] transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-upload-cloud-2-line text-base"></i>
              رفع صورة PNG/SVG
            </button>
            {identity.useLogoImage && identity.logoImageUrl && (
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl overflow-hidden border border-gray-200">
                  <img src={identity.logoImageUrl} alt="logo" className="w-full h-full object-contain" />
                </div>
                <button
                  onClick={() => updateIdentity({ useLogoImage: false, logoImageUrl: '' })}
                  className="text-xs text-red-500 hover:text-red-700 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line"></i> حذف
                </button>
              </div>
            )}
          </div>
          <input
            ref={logoFileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleLogoFileChange}
          />
          <p className="text-[10px] text-gray-400 mt-1.5">يُنصح بصورة مربعة شفافة (PNG) بحجم 200×200 بكسل أو أكبر</p>
        </div>

        <SaveButton state={identitySaveState} onClick={onSaveIdentity} label="حفظ الشعار" />
      </div>

      {/* ── Hero Image Section ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center bg-[#1a1a1a] rounded-lg">
            <i className="ri-image-2-line text-white text-[10px]"></i>
          </div>
          صورة الهيرو (الخلفية الرئيسية)
        </h3>
        <p className="text-xs text-gray-400 mb-4">اختر صورة خلفية القسم الرئيسي في أعلى الصفحة</p>

        <div className="grid grid-cols-2 gap-3 mb-4">
          {HERO_IMAGE_PRESETS.map(preset => (
            <button
              key={preset.id}
              onClick={() => updateIdentity({ heroImageId: preset.id, useCustomHeroImage: false })}
              className={`relative rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                identity.heroImageId === preset.id && !identity.useCustomHeroImage
                  ? 'border-amber-400'
                  : 'border-transparent hover:border-gray-300'
              }`}
            >
              <div className="w-full h-20 bg-gray-200 overflow-hidden">
                <img src={preset.url} alt={preset.label} className="w-full h-full object-cover object-top" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-2">
                <span className="text-white text-[10px] font-bold">{preset.label}</span>
              </div>
              {identity.heroImageId === preset.id && !identity.useCustomHeroImage && (
                <div className="absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center bg-amber-400 rounded-full">
                  <i className="ri-check-line text-black text-[10px]"></i>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Hero Overlay Opacity */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold text-gray-500">شفافية الطبقة الداكنة</p>
            <span className="text-xs font-bold text-[#1a1a1a]">{identity.heroOverlayOpacity}%</span>
          </div>
          <input
            type="range"
            min={0}
            max={80}
            value={identity.heroOverlayOpacity}
            onChange={e => updateIdentity({ heroOverlayOpacity: Number(e.target.value) })}
            className="w-full h-2 rounded-full appearance-none cursor-pointer"
            style={{ accentColor: selectedColor.primary }}
          />
          <div className="flex justify-between text-[10px] text-gray-400 mt-1">
            <span>شفاف</span>
            <span>داكن</span>
          </div>
        </div>

        {/* Hero Text Content */}
        <div className="flex flex-col gap-3 mb-4">
          <p className="text-xs font-bold text-gray-500">نصوص قسم الهيرو</p>
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] text-gray-400">العنوان الرئيسي</label>
            <input
              type="text"
              value={identity.heroTitle}
              onChange={e => updateIdentity({ heroTitle: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] text-gray-400">النص الفرعي</label>
            <textarea
              value={identity.heroSubtitle}
              onChange={e => updateIdentity({ heroSubtitle: e.target.value })}
              rows={2}
              maxLength={200}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white resize-none"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] text-gray-400">نص زر الدعوة للعمل (CTA)</label>
            <input
              type="text"
              value={identity.heroCta}
              onChange={e => updateIdentity({ heroCta: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white"
              style={{ fontFamily: "'Cairo', sans-serif" }}
            />
          </div>
        </div>

        {/* Hero Preview */}
        <div className="relative rounded-xl overflow-hidden mb-2" style={{ height: '120px' }}>
          <img
            src={identity.useCustomHeroImage && identity.heroImageUrl ? identity.heroImageUrl : selectedHeroPreset.url}
            alt="hero preview"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${identity.heroOverlayOpacity / 100})` }}></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4">
            <p className="text-white text-xs font-black text-center" style={{ fontFamily: selectedFont.css }}>
              {identity.heroTitle}
            </p>
            <div
              className="px-3 py-1 text-[10px] font-bold"
              style={{
                borderRadius: selectedRadius.value,
                background: selectedColor.primary,
                color: selectedColor.textOnPrimary,
              }}
            >
              {identity.heroCta}
            </div>
          </div>
        </div>

        <SaveButton state={identitySaveState} onClick={onSaveIdentity} label="حفظ صورة الهيرو" />
      </div>

      {/* ── Social Media Links ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center bg-gradient-to-br from-pink-500 to-amber-500 rounded-lg">
            <i className="ri-instagram-line text-white text-[10px]"></i>
          </div>
          روابط السوشيال ميديا
        </h3>
        <p className="text-xs text-gray-400 mb-4">أضف روابط حسابات التواصل الاجتماعي للمتجر</p>
        <div className="flex flex-col gap-3">
          {[
            { key: 'socialInstagram', icon: 'ri-instagram-line', label: 'إنستغرام', placeholder: 'https://instagram.com/yourstore', color: 'text-pink-500' },
            { key: 'socialTwitter',   icon: 'ri-twitter-x-line', label: 'تويتر / X', placeholder: 'https://x.com/yourstore',          color: 'text-gray-800' },
            { key: 'socialSnapchat',  icon: 'ri-snapchat-line',  label: 'سناب شات',  placeholder: 'https://snapchat.com/add/yourstore', color: 'text-yellow-500' },
            { key: 'socialTiktok',    icon: 'ri-tiktok-line',    label: 'تيك توك',   placeholder: 'https://tiktok.com/@yourstore',      color: 'text-gray-800' },
            { key: 'socialWhatsapp',  icon: 'ri-whatsapp-line',  label: 'واتساب',    placeholder: 'https://wa.me/966xxxxxxxxx',          color: 'text-green-500' },
          ].map(social => (
            <div key={social.key} className="flex items-center gap-3">
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-gray-100 flex-shrink-0 ${social.color}`}>
                <i className={`${social.icon} text-sm`}></i>
              </div>
              <div className="flex-1">
                <input
                  type="url"
                  value={identity[social.key as keyof IdentitySettings] as string}
                  onChange={e => updateIdentity({ [social.key]: e.target.value })}
                  placeholder={social.placeholder}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs outline-none focus:border-[#1a1a1a] transition-colors bg-white"
                  dir="ltr"
                />
              </div>
            </div>
          ))}
        </div>
        <SaveButton state={identitySaveState} onClick={onSaveIdentity} label="حفظ روابط السوشيال" />
      </div>

      {/* ── Background Pattern ── */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center bg-[#1a1a1a] rounded-lg">
            <i className="ri-grid-line text-white text-[10px]"></i>
          </div>
          نمط خلفية الصفحة
        </h3>
        <p className="text-xs text-gray-400 mb-4">أضف نمطاً خفيفاً لخلفية الموقع</p>
        <div className="grid grid-cols-5 gap-2">
          {BG_PATTERNS.map(pattern => (
            <button
              key={pattern.id}
              onClick={() => updateIdentity({ bgPattern: pattern.id })}
              className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                identity.bgPattern === pattern.id
                  ? 'border-[#1a1a1a] bg-white'
                  : 'border-gray-100 bg-white hover:border-gray-300'
              }`}
            >
              <div
                className="w-8 h-8 rounded-lg bg-gray-100 overflow-hidden"
                style={
                  pattern.id === 'dots'
                    ? { backgroundImage: 'radial-gradient(circle, #9ca3af 1px, transparent 1px)', backgroundSize: '6px 6px' }
                    : pattern.id === 'grid'
                    ? { backgroundImage: 'linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)', backgroundSize: '8px 8px' }
                    : pattern.id === 'lines'
                    ? { backgroundImage: 'repeating-linear-gradient(45deg, #e5e7eb, #e5e7eb 1px, transparent 1px, transparent 6px)' }
                    : pattern.id === 'noise'
                    ? { background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb, #f3f4f6)' }
                    : { background: '#f9fafb' }
                }
              ></div>
              <span className="text-[10px] text-gray-500 font-medium">{pattern.label}</span>
            </button>
          ))}
        </div>
        <SaveButton state={identitySaveState} onClick={onSaveIdentity} label="حفظ نمط الخلفية" />
      </div>
    </div>
  );
}
